import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { observable, action, autorun } from 'mobx';
import { withRouter } from 'react-router';
import Styled from 'styled';

import emojione from 'lib/emojione/emojione';
import { replace } from 'lib/emojione/ascii-mapping';
import FormView from 'lib/form/form-view';
import computed from 'lib/mobx/computed';
import { Form, Field } from 'lib/form/form';
import withDialog from 'lib/alerts/with-dialog';
import {
    ValueFieldData,
    RequiredValidator,
    PositiveValueValidator,
    MinMaxLengthValidator,
} from 'lib/form/value-field-data';
import { ArrayOfFieldData } from 'lib/form/array-of-field-data';
import { RetryDialog } from 'lib/alerts/dialog';
import { ErrorDialog } from 'lib/alerts/dialog';

import withStackViewForm from 'four-nets/ui/with-stack-view-form';
import Input from 'four-nets/ui-v2/form/input/input';

import { PhotoFileStore } from 'four-nets/photofile/photofile';
import SelectView from 'four-nets/ui/form/select-view';
import PhotoFileUploadView from 'four-nets/photofile/form/photofile-upload-view';
import CategoryPropertiesValue from 'four-nets/bazaar/product/create-edit-product/category-properties-values';
import withPhotofiles from 'four-nets/photofile/form/with-photofiles';
import { PhotoFileFieldData } from 'four-nets/photofile/form/photofile-field-data';
import { CountyStore, CountryStore } from 'four-nets/user/settings/user-settings';

import FieldWithLabel from 'four-nets/ui-v2/form/field-with-label/field-with-label';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';

import defaultStyles from './create-edit-product-view.scss';

@withPhotofiles({ type: 5, fieldName: 'images' })
class CreateEditProductForm extends Form {
    constructor(options) {
        const {
            title = '',
            description = '',
            category = null,
            price = null,
            county = null,
            photofiles = [],
            photoFileStore,
        } = options;

        const photoFileFieldDataForFiles = files =>
            files.map(
                file =>
                    new PhotoFileFieldData({
                        startUpload: false,
                        value: file,
                        type: 5,
                        photoFileStore,
                    })
            );

        super({
            ...options,
            photoFileStore,
            field: {
                category: new Field({
                    data: new ValueFieldData({
                        value: category,
                        validators: [new RequiredValidator()],
                    }),
                }),
                title: new Field({
                    data: new ValueFieldData({
                        value: title,
                        validators: [new RequiredValidator(), new MinMaxLengthValidator({ max: 70 })],
                    }),
                }),
                description: new Field({
                    data: new ValueFieldData({
                        value: description,
                        validators: [new RequiredValidator()],
                    }),
                }),
                price: new Field({
                    data: new ValueFieldData({
                        value: price,
                        validators: [new RequiredValidator(), new PositiveValueValidator()],
                    }),
                }),
                county: new Field({
                    data: new ValueFieldData({
                        value: county,
                    }),
                }),
                images: new Field({
                    data: new ArrayOfFieldData({
                        fields: photoFileFieldDataForFiles(photofiles),
                    }),
                }),
            },
        });
    }
}

export class CreateEditProductViewModel {
    productStore;
    photoFileStore;

    @observable form;
    @observable loadingCategories = false;
    @observable loadingCounties = false;
    @observable superCategory;
    @observable counties = [];

    @observable selectedSuperCategory = 'default';
    @observable selectedCategory = 'default';
    @observable selectedCounty = 'default';

    disposer;

    constructor(options) {
        const { productStore, superCategoryStore, product, site } = options;
        this.productStore = productStore;
        this.superCategoryStore = superCategoryStore;
        this.product = product;
        this.site = site;

        this.photoFileStore = new PhotoFileStore({
            api: productStore.api,
        });

        const countryStore = new CountryStore({
            api: productStore.api,
        });

        this.countyStore = new CountyStore({
            api: productStore.api,
            countryStore,
        });
    }

    handleSubmit = ({ data }) => {
        const { category, title, price, images, propertyValuesIds } = data;
        const description = emojione.toShort(replace(data.description));

        let promise = Promise.resolve();
        if (this.product) {
            promise = this.product.update(
                category,
                title,
                description,
                price,
                this.selectedCounty,
                images.map(image => image.id),
                propertyValuesIds
            );
        } else {
            this.productStore.setCategory(category);
            promise = this.productStore.create(
                category,
                title,
                description,
                price,
                this.selectedCounty,
                images.map(image => image.id),
                propertyValuesIds
            );
        }
        return promise;
    };

    @action setupForm = (options = {}) => {
        const { category, title, description, price, county, photofiles } = options;

        this.form = new CreateEditProductForm({
            photoFileStore: this.photoFileStore,
            title,
            description,
            price,
            category,
            county: county && county.id,
            photofiles,
            handleSubmit: this.handleSubmit,
        });

        if (category) {
            this.setSuperCategory(category.parent);
            this.selectedSuperCategory = category.parent;
            this.selectedCategory = category.id;
            this.changeCategory(category.id);
        }
        if (county) {
            this.setCounty(county.id);
        }
        return this.form;
    };

    @computed get superCategories() {
        return this.superCategoryStore.data.values();
    }

    @computed get categories() {
        if (this.superCategory) {
            return this.superCategory.categories;
        }
        return [];
    }

    @action setSuperCategory(id) {
        const superCategories = this.superCategoryStore.data;
        if (superCategories.has(id)) {
            this.superCategory = superCategories.get(id);
        }
        this.selectedCategory = 'default';
        this.form.field.get('category').data.setValue({ value: null });
    }

    changeCategory(categoryId) {
        if (this.superCategory) {
            let category = null;
            this.superCategory.categories.forEach(actualCategory => {
                if (actualCategory.id === categoryId) {
                    category = actualCategory;
                }
            });
            this.form.field.get('category').data.setValue({
                value: category,
            });
        }
    }

    @action setCounty = county => {
        this.selectedCounty = county;
    };

    @action setCounties(counties) {
        this.counties = counties;
    }

    @computed get loading() {
        return this.loadingCategories || this.loadingCounties;
    }

    loadCategories = () => {
        this.loadingCategories = true;
        return this.superCategoryStore.loadAll().finally(() => {
            this.loadingCategories = false;
        });
    };

    loadCounties = () => {
        this.loadingCounties = true;

        let promise = this.countyStore.loadSlovakCounties;
        if (this.site.isCzech) {
            promise = this.countyStore.loadCzechCounties;
        }

        return promise()
            .then(counties => {
                this.setCounties(counties);
            })
            .finally(() => {
                this.loadingCounties = false;
            });
    };
}

@withStackViewForm({
    title: <FormattedMessage id='PRODUCT_FORM_TITLE' />,
    promptOnClose: true,
})
@withDialog
@Styled(defaultStyles)
@observer
class CreateEditProductView extends React.Component {
    descriptionTextArea;

    state = { propertiesValues: new Map(), propertiesValid: true };

    setBodyTextArea = ref => {
        this.descriptionTextArea = ref;
    };

    setSuperCategory = event => {
        this.props.createEditProductViewModel.setSuperCategory(event.target.value);
    };

    setCategory = event => {
        event.persist();
        this.setState({ propertiesValues: new Map() }, () => {
            this.props.createEditProductViewModel.changeCategory(parseInt(event.target.value, 10));
        });
    };

    setCounty = event => {
        this.props.createEditProductViewModel.setCounty(parseInt(event.target.value, 10));
    };

    customSubmit = () => {
        this.props.setLoading(true);

        return this.form
            .submit({ propertyValuesIds: Array.from(this.state.propertiesValues.values()) })
            .then(product => {
                this.props.history.push(`/market/${product.slug}/`);
                if (this.props.forceUpdate) {
                    this.props.forceUpdate();
                }
                if (this.props.close) {
                    this.props.close();
                }
            })
            .catch(this.handleError)
            .finally(() => {
                this.props.setLoading(false);
            });
    };

    handleError = err => {
        console.error(err, err.stack);
        let code;
        if (err.underlayingError && err.underlayingError.status) {
            code = err.underlayingError.status;
        } else {
            code = err.code;
        }
        if (code && code >= 400 && code < 500) {
            this.props.showDialog(new ErrorDialog({ message: err.message }));
        } else {
            this.props.showDialog(new RetryDialog({ message: err.message, retry: this.submit }));
        }

        return Promise.reject(err);
    };

    componentDidMount() {
        this.disposer = autorun(() => {
            this.props.setLoading(this.props.createEditProductViewModel.loading);
        });

        const { product } = this.props.createEditProductViewModel;

        if (product && product.propertyValues) {
            const initial = new Map();
            product.propertyValues.forEach(propertyValue => {
                initial.set(propertyValue.property, `${propertyValue.id}`);
            });
            this.setState({ propertiesValues: initial });
        }

        Promise.all([
            this.props.createEditProductViewModel.loadCategories(),
            this.props.createEditProductViewModel.loadCounties(),
        ])
            .then(() => {
                const { product } = this.props.createEditProductViewModel;
                this.props.createEditProductViewModel.setupForm(product);
            })
            .catch(err => {
                this.props.showDialog(
                    new RetryDialog({
                        message: err.message,
                        retry: () => {
                            return Promise.all([
                                this.props.createEditProductViewModel.loadCategories(),
                                this.props.createEditProductViewModel.loadCounties(),
                            ]);
                        },
                    })
                );
            });
    }

    componentWillUnmount() {
        this.disposer();
    }

    @computed get form() {
        if (this.props.createEditProductViewModel) {
            return this.props.createEditProductViewModel.form;
        }
        return null;
    }

    onCompleted = data => {
        if (data && data.categoryPropertyValues && data.categoryPropertyValues.propertiesValues) {
            const { propertiesValues } = data.categoryPropertyValues;
            const initial = new Map(this.state.propertiesValues);
            propertiesValues.map(({ property }) => {
                if (!initial.has(property)) {
                    initial.set(property, null);
                }
            });
            this.setState({ propertiesValues: initial }, this.determinePropertiesValid);
        }
    };

    determinePropertiesValid = () => {
        let propertiesValid = true;
        this.state.propertiesValues.forEach(value => {
            if (value === null || value === undefined) {
                propertiesValid = false;
            }
        });
        this.setState({ propertiesValid });
    };

    render() {
        const { createEditProductViewModel, styles } = this.props;
        const { valid, dirty } = this.form || {};
        const { propertiesValid } = this.state;

        const selectedCountyId = createEditProductViewModel.selectedCounty;

        return this.form ? (
            <FormView form={this.form} submitOnEnter={false} submit={this.customSubmit}>
                <div className={styles.wrapper}>
                    <div className={styles.fieldWrapper} key='title'>
                        <FieldWithLabel
                            mini={true}
                            key='title'
                            field={
                                <Input
                                    style={{ textInput: styles.inputColors }}
                                    {...this.form.field.get('title').data}
                                    type='text'
                                    id='id_title'
                                    className={styles.textInput}
                                />
                            }
                            label={<FormattedMessage id='ADD_TITLE' />}
                            id='product-title'
                        />
                    </div>
                    <div className={styles.fieldWrapper} key='content'>
                        <FieldWithLabel
                            mini={true}
                            key='content'
                            field={
                                <TextArea
                                    style={{ textArea: styles.inputColors }}
                                    field={this.form.field.get('description')}
                                    type='text'
                                />
                            }
                            label={<FormattedMessage id='ADD_DESCRIPTION' />}
                            id='product-description'
                        />
                    </div>
                    <div className={styles.fieldWrapper} key='price'>
                        <FieldWithLabel
                            mini={true}
                            key='title'
                            field={
                                <Input
                                    style={{ textInput: styles.inputColors }}
                                    {...this.form.field.get('price').data}
                                    type='number'
                                    id='id_price'
                                    className={styles.textInput}
                                    dynamicWidth={false}
                                />
                            }
                            label={<FormattedMessage id='ADD_PRICE' />}
                            id='product-price'
                        />
                    </div>
                    <div className={styles.fieldWrapper} key='superCategory'>
                        <FieldWithLabel
                            mini={true}
                            key='supercategory'
                            field={
                                <SelectView
                                    style={{ container: styles.inputColors }}
                                    onChange={this.setSuperCategory}
                                    defaultValue={createEditProductViewModel.selectedSuperCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {createEditProductViewModel.superCategories.map(superCategory => (
                                        <option value={superCategory.id} key={superCategory.id}>
                                            {superCategory.name}
                                        </option>
                                    ))}
                                </SelectView>
                            }
                            label={<FormattedMessage id='BAZAAR_CATEGORY' />}
                            id='product-super-category'
                        />
                    </div>
                    <div className={styles.fieldWrapper} key='category'>
                        <FieldWithLabel
                            mini={true}
                            field={
                                <SelectView
                                    style={{ container: styles.inputColors }}
                                    onChange={this.setCategory}
                                    key={
                                        (createEditProductViewModel.superCategory &&
                                            createEditProductViewModel.superCategory.name) ||
                                        'default'
                                    }
                                    field={this.form.field.get('category')}
                                    defaultValue={createEditProductViewModel.selectedCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {createEditProductViewModel.superCategory &&
                                        createEditProductViewModel.superCategory.categories.map(category => (
                                            <option value={category.id} key={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                </SelectView>
                            }
                            label={<FormattedMessage id='BAZAAR_SUBCATEGORY' />}
                            id='product-category'
                        />
                    </div>
                    {this.form &&
                    this.form.field.get('category') &&
                    this.form.field.get('category').data &&
                    this.form.field.get('category').data.value &&
                    this.form.field.get('category').data.value.id ? (
                        <CategoryPropertiesValue
                            categoryId={this.form.field.get('category').data.value.id}
                            onClick={(property, value) => {
                                this.setState(prevState => {
                                    const propertiesValues = new Map(prevState.propertiesValues);
                                    propertiesValues.set(property, value);
                                    return { propertiesValues };
                                }, this.determinePropertiesValid);
                            }}
                            onCompleted={this.onCompleted}
                            selected={new Set(this.state.propertiesValues.values())}
                        />
                    ) : null}
                    {!propertiesValid ? (
                        <p className={styles.error}>{<FormattedMessage id='FORM_ALL_FIELDS_REQUIRED_ERROR' />}</p>
                    ) : null}
                    <div className={styles.fieldWrapper} key='county'>
                        <FieldWithLabel
                            mini={true}
                            key='county'
                            field={
                                <SelectView
                                    style={{ container: styles.inputColors }}
                                    onChange={this.setCounty}
                                    value={selectedCountyId.toString()}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {createEditProductViewModel.counties.map(county => (
                                        <option value={county.id} key={county.id}>
                                            {county.name}
                                        </option>
                                    ))}
                                </SelectView>
                            }
                            label={
                                <span className={styles.capitalizeFirst}>
                                    <FormattedMessage id='BAZAAR_COUNTY' />
                                </span>
                            }
                            id='product-county'
                        />
                    </div>
                    <div className={styles.fieldWrapper} key='images'>
                        <FieldWithLabel
                            mini={true}
                            key='images'
                            field={
                                <PhotoFileUploadView
                                    style={{ input: styles.input }}
                                    field={this.form.field.get('images')}
                                    fieldDataForFiles={this.form.photoFileFieldDataForFiles}
                                />
                            }
                            label={
                                <span className={styles.capitalizeFirst}>
                                    <FormattedMessage id='PRODUCT_IMAGES' />
                                </span>
                            }
                            id='product-photos'
                        />
                    </div>
                    <div className={styles.submitButtonWrapper}>
                        <button
                            type='button'
                            className={styles.submitButton}
                            disabled={!(valid && dirty && propertiesValid)}
                            onClick={this.customSubmit}
                        >
                            {this.props.submitTitle || <FormattedMessage id='BUTTON_LABEL_SAVE' />}
                        </button>
                    </div>
                </div>
            </FormView>
        ) : null;
    }
}

export default withRouter(CreateEditProductView);
